// column.js
import React from 'react';
import { Typography, Badge } from 'antd';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import TaskCard from './TaskCardRefactored';

const { Text } = Typography;

const Column = ({ column, tasks, onViewDetails, onToggleDone, activeTask, searchTerm }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: column.id,
  });

  const columnTasks = tasks.filter(task => task.column === column.id);

  // Calculate column statistics
  const totalDifficulty = columnTasks.reduce((sum, task) => sum + task.difficulty, 0);
  const avgDifficulty = columnTasks.length > 0 ? (totalDifficulty / columnTasks.length).toFixed(1) : 0;

  return (
    <div
      id={`column-${column.id}`}
      ref={setNodeRef}
      style={{
        background: isOver 
          ? 'linear-gradient(135deg, #f0f9ff 0%, #e6f4ff 100%)' 
          : 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        padding: '16px 12px',
        borderRadius: '12px',
        minHeight: '500px',
        display: 'flex',
        flexDirection: 'column',
        border: isOver ? '2px dashed #1890ff' : '1px solid #e8e8e8',
        boxShadow: isOver 
          ? '0 4px 12px rgba(24, 144, 255, 0.12)'
          : '0 2px 8px rgba(0, 0, 0, 0.04)',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
    >
      {/* Column Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '16px',
        paddingBottom: '12px',
        borderBottom: '2px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text strong style={{ fontSize: '14px', color: '#262626', fontWeight: '600' }}>
            {column.icon} {column.title}
          </Text>
          <Badge 
            count={columnTasks.length} 
            showZero 
            style={{ 
              background: '#f0f0f0',
              color: '#666',
              boxShadow: 'none'
            }}
          />
        </div>

        {columnTasks.length > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            padding: '2px 6px',
            background: '#fafafa',
            borderRadius: '8px',
            border: '1px solid #f0f0f0'
          }}>
            <Text type="secondary" style={{ fontSize: '10px', fontWeight: '500' }}>
              ⭐{avgDifficulty}
            </Text>
          </div>
        )}
      </div>

      {/* Task Cards Container */}
      <SortableContext items={columnTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div style={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          overflowY: 'auto',
          padding: '2px',
          margin: '-2px',
        }}>
          {columnTasks.map(task => (
            <TaskCard
              key={task.id}
              task={task}
              onView={onViewDetails}
              onToggleDone={onToggleDone}
              isDragging={activeTask?.id === task.id}
            />
          ))}
          
          {columnTasks.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#bfbfbf',
              border: '2px dashed #e8e8e8',
              borderRadius: '8px',
              background: '#fafafa',
              marginTop: 'auto',
              marginBottom: 'auto'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>📋</div>
              <Text style={{ fontSize: '12px', color: '#8c8c8c', display: 'block', marginBottom: '4px' }}>
                No tasks
              </Text>
              <Text style={{ fontSize: '10px', color: '#bfbfbf' }}>
                Drop tasks here
              </Text>
            </div>
          )}
        </div>
      </SortableContext>

      {/* Drop Zone Indicator */}
      {isOver && columnTasks.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(24, 144, 255, 0.9)',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '12px',
          fontWeight: '600',
          zIndex: 10,
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
        }}>
          🎯 Drop here to move task
        </div>
      )}
    </div>
  );
};

export default Column;